import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TimesheetsService } from "../services/timesheets";
import {
  GetResultsForExport,
  GetSummaryByTask,
  GetSummaryByTaskType,
  GetSummaryByUser,
  IsLoading,
  QueryResultList,
  Summary,
  loadQueryResultsInfo,
  setLoading,
} from "../redux/reducers/queryResult.reducer";
import {
  hasTimeAdminAccess,
  hasTimeViewerAccess,
  preparedFilters,
} from "../utils/timesUtils";
import {
  Holidays,
  ProjectList,
  SelectProjectOrWorkOrder,
} from "../redux/reducers/times.reducer";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { useTimesMetadata } from "./useTimesMetadata";
import { selectMetaTimesData } from "../redux/reducers/metaTimes.reducer";
import {
  hasSuperTimeAdminAccess,
  isSupervisorOrProjectOwner,
  isDeliveryTimeAdminChecker,
  hasSuperAdminAccess,
} from "../../SharedModule/utils/accessUtils";
import { SelectCostCentersQuery } from "../redux/reducers/timesQuery.reducer";

export const useQueryResults = (filters: any, isInfoQueryLoaded: boolean) => {
  useTimesMetadata();
  const dispatch = useDispatch();

  const projectList = useSelector(ProjectList);
  const selectProjectOrWorkOrder = useSelector(SelectProjectOrWorkOrder);
  const holidays = useSelector(Holidays);
  const costCentersQuery = useSelector(SelectCostCentersQuery);

  const MetaTimesInfo = useSelector(selectMetaTimesData);

  const summary = useSelector(Summary);
  const summaryByUser = useSelector(GetSummaryByUser);
  const summaryByTask = useSelector(GetSummaryByTask);
  const summaryByTaskType = useSelector(GetSummaryByTaskType);
  const queryResultList = useSelector(QueryResultList);
  const resultsForExport = useSelector(GetResultsForExport);

  const { activeUserId, userRolesList } = useSelector(selectUserRoles);

  const isTimeAdmin = hasTimeAdminAccess(userRolesList);
  const isSuperTimeAdmin = hasSuperTimeAdminAccess(userRolesList);
  const isSupervisorOrPO = isSupervisorOrProjectOwner(userRolesList);
  const isDeliveryTimeAdmin = isDeliveryTimeAdminChecker(userRolesList);
  const isTimeViewer = hasTimeViewerAccess(userRolesList);
  const isOnlySuperAdmin = hasSuperAdminAccess(userRolesList);

  let isOnlyDeliveryTimeAdmin =
    isDeliveryTimeAdmin && !isTimeViewer && !isTimeAdmin;

  let filtersForRequest;
  if (isInfoQueryLoaded) {
    filtersForRequest = preparedFilters(
      filters,
      isOnlyDeliveryTimeAdmin,
      costCentersQuery
    );
  }

  const isLoading = useSelector(IsLoading);

  const loadQueryResultsPageInfo = useCallback(async () => {
    dispatch(setLoading(true));
    if (isInfoQueryLoaded) {
      const queryResults = await TimesheetsService.getQueryResults(
        filtersForRequest
      );
      dispatch(loadQueryResultsInfo(queryResults.data.data));
    }
  }, [dispatch, isInfoQueryLoaded]);

  useEffect(() => {
    loadQueryResultsPageInfo();
  }, [loadQueryResultsPageInfo]);

  // The page query skips the MongoDB audit lookup (Entered/Approved By/Date come back empty).
  // The Excel export needs those columns, so it re-runs the same query with includeAudit.
  const getResultsForExportWithAudit = async () => {
    dispatch(setLoading(true));
    try {
      const queryResults = await TimesheetsService.getQueryResults({
        ...filtersForRequest,
        includeAudit: true,
      });
      return queryResults.data.data.result;
    } finally {
      dispatch(setLoading(false));
    }
  };

  return {
    isLoading,
    summary,
    summaryByUser,
    summaryByTask,
    summaryByTaskType,
    queryResultList,
    projectList,
    selectProjectOrWorkOrder,
    holidays,
    isOnlySuperAdmin,
    userRolesList,
    resultsForExport,
    isTimeAdmin,
    isSuperTimeAdmin,
    isSupervisorOrPO,
    isDeliveryTimeAdmin,
    isTimeViewer,
    activeUserId,
    MetaTimesInfo,
    loadQueryResultsPageInfo,
    getResultsForExportWithAudit,
  };
};
